import {
  Button,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeaderWithIcon,
  Skeleton,
} from '@dev-spendesk/grapes';
import { useState } from 'react';
import { useQueryClient } from 'react-query';

import { UnexpectedErrorCallout } from 'common/components/UnexpectedErrorCallout/UnexpectedErrorCallout';
import { type StepConfig, Wizard } from 'common/components/Wizard';
import { useTranslation } from 'common/hooks/useTranslation';
import { useNotifications } from 'modules/app/notifications';
import { postFinalizeUserAuthenticationFactorCreation } from 'modules/authentication-factor/api/postFinalizeUserAuthenticationFactorCreation';
import { ShowQrCode } from 'modules/authentication-factor/components/CreateAuthenticationFactorModal/steps/CreateTotp/ShowQrCode';
import { usePostUserAuthenticationFactor } from 'modules/authentication-factor/hooks/usePostUserAuthenticationFactor';
import { logger } from 'src/utils/datadog-log-wrapper';

import { DownloadAuthenticatorApp } from './DownloadAuthenticatorApp';
import { FinalizeSetup } from './FinalizeTotp';

interface CreateTotpFactorProps {
  handleClose: () => void;
  handleBack: () => void;
}

type AuthenticationFactorData = {
  qrCodeUrl: string;
  secret: string;
  id: string;
};

export const CreateTotpFactor = ({
  handleClose,
  handleBack,
}: CreateTotpFactorProps) => {
  const { t } = useTranslation('global');
  const [currentStep, setCurrentStep] = useState(1);
  const [verificationCode, setVerificationCode] = useState<string[]>([]);
  const [isVerifyingCode, setIsVerifyingCode] = useState(false);
  const [authenticationFactorData, setAuthenticationFactorData] = useState<
    AuthenticationFactorData | undefined
  >(undefined);
  const [isVerificationCodeWrong, setIsVerificationCodeWrong] = useState(false);
  const {
    isLoading: qrCodeIsLoading,
    isSuccess: qrCodeIsSuccess,
    isError: qrCodeIsError,
    mutateAsync: createTotpFactorQueryState,
  } = usePostUserAuthenticationFactor({
    name: t('profile.authenticationFactors.defaultTOTPFactorName'),
  });
  const { successNotif } = useNotifications();
  const queryClient = useQueryClient();

  const handleVerificationCodeChange = async (code: string[]) => {
    setIsVerificationCodeWrong(false);
    setVerificationCode(code);
    if (code.join('').trim().length === 6) {
      await finalizeTokenCreation(code);
    }
  };

  const steps: StepConfig[] = [
    {
      label: t('profile.authenticationFactors.create.totp.openAppItemHeader'),
      content: <DownloadAuthenticatorApp />,
    },
    {
      label: t(
        'profile.authenticationFactors.create.totp.scanQrCodeItemHeader',
      ),
      content: (
        <>
          {qrCodeIsSuccess && authenticationFactorData && (
            <ShowQrCode
              qrCodeUrl={authenticationFactorData?.qrCodeUrl}
              secretKey={authenticationFactorData?.secret}
            />
          )}
          {qrCodeIsLoading && <Skeleton height="100%" width="100%" />}
          {qrCodeIsError && <UnexpectedErrorCallout />}
        </>
      ),
    },
    {
      label: t('profile.authenticationFactors.create.totp.finalizeItemHeader'),
      content: (
        <FinalizeSetup
          verificationCode={verificationCode}
          setVerificationCode={handleVerificationCodeChange}
          hasError={isVerificationCodeWrong}
          isDisabled={isVerifyingCode}
        />
      ),
    },
  ];

  const getButtonText = () => {
    if (currentStep === 1) {
      return {
        backButtonText: t(
          'profile.authenticationFactors.create.chooseOtherMethod',
        ),
        nextButtonText: t(
          'profile.authenticationFactors.create.totp.openAppNextButton',
        ),
      };
    }
    if (currentStep === 2) {
      return {
        backButtonText: t('misc.back'),
        nextButtonText: t('misc.continue'),
      };
    }
    return {
      backButtonText: t('misc.back'),
      nextButtonText: t(
        'profile.authenticationFactors.create.totp.finalizeNextButton',
      ),
    };
  };

  const handleNext = async () => {
    if (currentStep === 1) {
      try {
        setCurrentStep(currentStep + 1);
        if (!authenticationFactorData) {
          setAuthenticationFactorData(await createTotpFactorQueryState());
        }
      } catch (error) {
        logger.warn('Failed to create TOTP authentication factor', {
          error,
          team: 'cards-ux',
          scope: 'user-authentication-factors',
        });
      }
    } else if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
    } else {
      await finalizeTokenCreation(verificationCode);
    }
  };

  const finalizeTokenCreation = async (code: string[]) => {
    try {
      setIsVerifyingCode(true);
      await submitVerificationCode(code);
      await queryClient.invalidateQueries('userAuthenticationFactors');
      successNotif(
        t('profile.authenticationFactors.create.totp.successMessage'),
      );
      handleClose();
    } catch {
      setIsVerifyingCode(false);
      setIsVerificationCodeWrong(true);
    }
  };

  const submitVerificationCode = async (verificationCodeToSubmit: string[]) => {
    const token = verificationCodeToSubmit.join('').trim();
    if (token.length !== 6) {
      throw new Error('Verification code must be 6 digits long');
    }
    if (!authenticationFactorData) {
      throw new Error('Factor ID is not set');
    }

    await postFinalizeUserAuthenticationFactorCreation({
      factorId: authenticationFactorData.id,
      token,
    });
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    } else {
      handleBack();
    }
  };

  return (
    <ModalContent
      aria-labelledby="auth-factor-modal-title"
      onClose={handleClose}
    >
      <ModalHeaderWithIcon
        title={t('profile.authenticationFactors.create.totp.title')}
        iconName="lock-close"
        iconVariant="neutral"
        titleId="auth-factor-modal-title"
      />
      <ModalBody>
        <Wizard
          steps={steps}
          currentStep={currentStep}
          className="mt-16 text-start"
        />
      </ModalBody>
      <ModalFooter>
        <Button
          variant="secondaryNeutral"
          text={getButtonText().backButtonText}
          iconName="arrow-left"
          iconPosition="left"
          onClick={handlePrevious}
        />
        <Button
          variant="primaryBrand"
          text={getButtonText().nextButtonText}
          onClick={handleNext}
          isDisabled={
            (currentStep === 3 && verificationCode.length !== 6) ||
            isVerifyingCode
          }
        />
      </ModalFooter>
    </ModalContent>
  );
};
